import { getSpeckleToken } from "@/api/services/Speckle";

/** `codes` holds each error's `extensions.code`, so a caller can tell "refused" from "broken". */
export type SpeckleGraphqlResult<T> = { data: T | null; errors: string[]; codes: string[] };

type SpeckleGraphqlError = { message?: string; extensions?: { code?: unknown } };

// One call against the Speckle server with the user's own token. Null data when the answer is unknown
// (no token, transport failure); errors carried out so a caller can tell "refused" from "unreachable".
export const speckleGraphql = async <T>(query: string, variables: Record<string, string>): Promise<SpeckleGraphqlResult<T>> => {
  const token = await getSpeckleToken();
  if (!token) return { data: null, errors: [], codes: [] };

  const response = await fetch(`${import.meta.env.VITE_SPECKLE_URL}/graphql`, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify({ query, variables }),
  });
  if (!response.ok) return { data: null, errors: [`HTTP ${response.status}`], codes: [] };

  const payload = (await response.json()) as { data?: T; errors?: SpeckleGraphqlError[] };
  const errors = payload.errors?.map((error) => error.message ?? "Unknown Speckle error") ?? [];
  const codes =
    payload.errors?.flatMap((error) => (typeof error.extensions?.code === "string" ? [error.extensions.code] : [])) ?? [];
  return { data: errors.length > 0 ? null : payload.data ?? null, errors, codes };
};
